/**
 * 10.9 — LOBBY PvP (Fase 2): mostra quem está ONLINE no lobby agora (presença Supabase) e um
 * botão "Procurar oponente" (marca você como procurando). O DUELO em si (Fase 3) vem depois —
 * por enquanto o lobby prova a presença/matchmaking em tempo real.
 */
import { joinLobby, leaveLobby, selfId, setSearching, type LobbyPlayer } from "../net/CloudPvp";
import { findMatch, findTeamMatch, type TeamRoom, type TeamMember } from "../net/CloudDuel";
import { pvpRank } from "../net/CloudProfile";
import { fmtNum } from "../utils/format";

export interface PvpInfo {
  name: string;
  level: number;
  power: number;
  wins: number;
  losses: number;
}

export class PvpScreen {
  private readonly root: HTMLDivElement;
  private readonly listEl: HTMLElement;
  private readonly searchBtn: HTMLButtonElement;
  private readonly noteEl: HTMLElement;
  private searching = false;
  private players: LobbyPlayer[] = [];
  private openedAt = 0; // 10.9C — anti tap-through: ignora o clique que abriu a tela

  private matched = false;
  private cand = ""; // 10.9FF — par candidato (precisa ficar estavel antes de entrar na luta)
  private candAt = 0;
  private candTimer = 0;
  private mode: "1v1" | "3v3" = "1v1"; // 10.9BC
  private readonly teamBtn: HTMLButtonElement;

  constructor(
    uiRoot: HTMLElement,
    private readonly onClose: () => void,
    private readonly getInfo: () => PvpInfo,
    private readonly onMatch: (opp: LobbyPlayer) => void,
    private readonly onTeamMatch: (room: TeamRoom) => void = () => {},
  ) {
    injectStyles();
    this.root = document.createElement("div");
    this.root.className = "overlay pvp-overlay";
    this.root.hidden = true;
    this.root.innerHTML = `
      <div class="panel void-panel pvp-panel">
        <div class="pvp-head">
          <h2 class="pvp-title">⚔️ Arena PvP</h2>
          <button class="btn pvp-x" data-a="close">✕</button>
        </div>
        <div class="pvp-you"></div>
        <div class="pvp-modes">
          <button class="btn primary pvp-search" data-a="search">⚔️ Duelo 1x1</button>
          <button class="btn primary pvp-search pvp-team" data-a="team">🛡️ Times 3x3</button>
        </div>
        <div class="pvp-note">Escolha um modo e entre na fila. 3x3: precisa de 6 na fila; eliminação, sem renascer.</div>
        <div class="pvp-online-lbl"></div>
        <div class="pvp-list"></div>
      </div>`;
    uiRoot.appendChild(this.root);
    this.listEl = this.root.querySelector(".pvp-list")!;
    this.searchBtn = this.root.querySelector('[data-a="search"]')!;
    this.noteEl = this.root.querySelector(".pvp-note")!;
    this.root.querySelector('[data-a="close"]')!.addEventListener("click", () => this.onClose());
    this.root.addEventListener("click", (e) => {
      // 10.9C — só fecha por clique-fora DEPOIS de 500ms (senão o toque que abriu fecha na hora)
      if (e.target === this.root && Date.now() - this.openedAt > 500) this.onClose();
    });
    this.teamBtn = this.root.querySelector('[data-a="team"]')!;
    this.searchBtn.addEventListener("click", () => this.toggleSearch("1v1"));
    this.teamBtn.addEventListener("click", () => this.toggleSearch("3v3"));
  }

  open(note?: string): void {
    window.clearTimeout(this.leaveTimer);
    this.root.hidden = false;
    this.cand = "";
    this.openedAt = Date.now();
    this.searching = false;
    this.matched = false;
    this.refreshButtons();
    this.renderYou();
    this.noteEl.dataset.keep = note ? "1" : "";
    if (note) this.noteEl.textContent = note; // 10.9FF — ex.: o oponente nao respondeu
    this.listEl.innerHTML = `<div class="pvp-empty">Conectando ao lobby…</div>`;
    const info = this.getInfo();
    void joinLobby(info, (players) => {
      this.players = players;
      this.render();
      this.checkMatch();
    });
  }

  /** 10.9D — achou par na fila? inicia o duelo (os dois clientes concordam no par). */
  private checkMatch(): void {
    if (this.matched || !this.searching) return;
    const myId = selfId();
    if (!myId) return;
    if (this.mode === "3v3") {
      // 10.9GO — alguem JA fechou uma sala que me inclui (vs = topico da sala): entro na mesma, na hora
      const picked = this.players.find((p) => p.id !== myId && !!p.vs && p.vs.startsWith("team-") && p.vs.includes(myId.slice(0, 8)));
      if (picked && picked.vs) { const r = this.roomFromTopic(picked.vs); if (r) { this.commitTeam(r); return; } }
      // quem ja esta numa sala (vs) nao entra na conta de outra (antes: 7o jogador ganhava de graca)
      const room = findTeamMatch(this.players.filter((p) => !p.vs), myId);
      if (!room) { this.cand = ""; return; }
      // 10.9FH — mesmo BUG do 1x1 no 3x3: os 6 clientes viam a lista em tempos diferentes e cada um
      // fechava uma sala diferente (ou saia da fila antes dos outros verem) -> ninguem entrava.
      // Agora a MESMA sala de 6 precisa ficar estavel por 1,5 s, e ao fechar continuamos no lobby
      // (searching) por alguns segundos pros 6 se enxergarem e montarem a mesma sala.
      const key = room.members.map((m) => m.id).sort().join(",");
      if (this.cand !== key) { this.cand = key; this.candAt = Date.now(); }
      if (Date.now() - this.candAt < 1500) {
        window.clearTimeout(this.candTimer);
        this.candTimer = window.setTimeout(() => this.checkMatch(), 1550);
        return;
      }
      this.commitTeam(room);
      return;
    }
    // 10.9FF — alguem ja casou COMIGO (e esta indo pra luta): vou junto na hora
    const pick = this.players.find((p) => p.vs === myId);
    if (pick) { this.commit(pick); return; }
    // quem ja casou com OUTRO sai da conta do par
    const opp = findMatch(this.players.filter((p) => !p.vs), myId);
    if (!opp || !opp.searching || opp.id === myId) { this.cand = ""; return; }
    // 10.9FF — BUG: cada cliente ve a lista de presenca num tempo diferente; com 3+ na fila um
    // podia casar com A enquanto A casava com B — um deles ficava "Aguardando" e voltava pro mapa.
    // Agora o par precisa ficar o MESMO por 1,2 s antes de entrar na luta.
    const key = opp.id + ":" + (opp.q ?? 0);
    if (this.cand !== key) { this.cand = key; this.candAt = Date.now(); }
    if (Date.now() - this.candAt < 1200) {
      window.clearTimeout(this.candTimer);
      this.candTimer = window.setTimeout(() => this.checkMatch(), 1250);
      return;
    }
    this.commit(opp);
  }

  /** 10.9GO — fecha a sala 3x3: marca vs = TOPICO (os outros 5 leem e entram na MESMA sala; um 7o nao usa a gente). */
  private commitTeam(room: TeamRoom): void {
    if (this.matched) return;
    this.matched = true;
    window.clearTimeout(this.candTimer);
    void setSearching(this.getInfo(), true, "3v3", room.topic);
    this.onTeamMatch(room);
  }
  /** 10.9GO — reconstroi a sala a partir do topico "team-<id8+q36>-...": prefixo de 8 chars = id; time = k%2. */
  private roomFromTopic(topic: string): TeamRoom | null {
    const segs = topic.split("-").slice(1);
    if (segs.length !== 6) return null;
    const members: TeamMember[] = [];
    for (let k = 0; k < 6; k++) {
      const pre = segs[k].slice(0, 8);
      const p = this.players.find((x) => x.id.slice(0, 8) === pre);
      if (!p) return null;
      members.push({ id: p.id, name: p.name || "Caçador", power: p.power, team: k % 2 === 0 ? "A" : "B" });
    }
    return { topic, members };
  }
  /** 10.9FF — fecha o par: avisa no lobby "vou lutar com X" (o rival le e entra junto) e vai pra luta. */
  private commit(opp: LobbyPlayer): void {
    if (this.matched) return;
    this.matched = true;
    window.clearTimeout(this.candTimer);
    void setSearching(this.getInfo(), true, "1v1", opp.id);
    this.onMatch(opp);
  }

  private refreshButtons(): void {
    const s = this.searching;
    this.searchBtn.textContent = s && this.mode === "1v1" ? "✖ Cancelar (1x1)" : "⚔️ Duelo 1x1";
    this.teamBtn.textContent = s && this.mode === "3v3" ? "✖ Cancelar (3x3)" : "🛡️ Times 3x3";
    this.searchBtn.classList.toggle("searching", s && this.mode === "1v1");
    this.teamBtn.classList.toggle("searching", s && this.mode === "3v3");
    this.searchBtn.disabled = s && this.mode === "3v3";
    this.teamBtn.disabled = s && this.mode === "1v1";
  }

  private leaveTimer = 0;
  /** keepMs: continua no lobby (invisivel) esse tempo, pro rival enxergar o "vs" e entrar na luta */
  hide(keepMs = 0): void {
    window.clearTimeout(this.candTimer);
    window.clearTimeout(this.leaveTimer);
    this.root.hidden = true;
    if (keepMs > 0) { this.leaveTimer = window.setTimeout(() => void leaveLobby(), keepMs); return; }
    void leaveLobby();
  }

  private renderYou(): void {
    const i = this.getInfo();
    const rank = pvpRank(i.wins);
    this.root.querySelector(".pvp-you")!.innerHTML =
      `<div class="pvp-you-col">
         <span class="pvp-you-name">${escapeHtml(i.name || "Caçador")}</span>
         <span class="pvp-rank" style="color:${rank.color}">${rank.icon} ${rank.name}</span>
       </div>
       <div class="pvp-you-col right">
         <span class="pvp-you-stats">Nv ${i.level} · ${fmtNum(i.power)}</span>
         <span class="pvp-wl">${i.wins}V / ${i.losses}D</span>
       </div>`;
  }

  private toggleSearch(mode: "1v1" | "3v3"): void {
    this.noteEl.dataset.keep = "";
    if (this.searching && this.mode !== mode) return;
    this.mode = mode;
    this.searching = !this.searching;
    this.refreshButtons();
    this.updateNote();
    void setSearching(this.getInfo(), this.searching, this.mode);
    if (this.searching) this.checkMatch(); // um oponente já pode estar esperando
  }

  private updateNote(): void {
    if (!this.searching) { if (this.noteEl.dataset.keep === "1") return; this.noteEl.textContent = "Escolha um modo e entre na fila. 3x3: precisa de 6 na fila; eliminação, sem renascer."; return; }
    if (this.mode === "3v3") {
      const n = this.players.filter((p) => p.searching && p.mode === "3v3").length;
      this.noteEl.textContent = `Fila 3x3: ${Math.min(6, n)}/6 — a partida começa quando fechar 6.`;
    } else this.noteEl.textContent = "Procurando oponente 1x1…";
  }

  private render(): void {
    const me = selfId();
    this.updateNote();
    this.root.querySelector(".pvp-online-lbl")!.textContent = `Online agora: ${this.players.length}`;
    if (this.players.length === 0) {
      this.listEl.innerHTML = `<div class="pvp-empty">Conectando ao lobby…</div>`;
      return;
    }
    // 10.9C — mostra VOCÊ no topo (prova que sua presença registrou) + os outros
    const sorted = [...this.players].sort((a, b) => Number(b.id === me) - Number(a.id === me));
    this.listEl.innerHTML = sorted
      .map((p) => {
        const isMe = p.id === me;
        const meTag = isMe ? ` <span class="pvp-me-tag">(você)</span>` : "";
        const tag = p.searching ? `<span class="pvp-searching">🔍 fila ${p.mode === "3v3" ? "3x3" : "1x1"}</span>` : "";
        return `<div class="pvp-row${isMe ? " me" : ""}"><span class="pvp-p-name">${escapeHtml(p.name || "Caçador")}${meTag}${tag}</span><b class="pvp-p-stats">Nv ${p.level} · ${fmtNum(p.power)}</b></div>`;
      })
      .join("");
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c);
}

let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected || typeof document === "undefined") return;
  stylesInjected = true;
  const css = `
  .pvp-panel{max-width:440px;width:92%;max-height:86vh;display:flex;flex-direction:column;gap:12px}
  .pvp-head{display:flex;align-items:center;justify-content:space-between}
  .pvp-title{margin:0;font-size:20px;color:#eaf1ff}
  .pvp-x{padding:6px 12px}
  .pvp-you{display:flex;align-items:center;justify-content:space-between;gap:8px;background:rgba(124,77,255,.12);border:1px solid rgba(124,77,255,.4);border-radius:12px;padding:10px 14px}
  .pvp-you-col{display:flex;flex-direction:column;gap:3px;min-width:0}
  .pvp-you-col.right{align-items:flex-end}
  .pvp-you-name{font-weight:800;color:#fff;font-size:16px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .pvp-rank{font-weight:800;font-size:13px}
  .pvp-you-stats{color:#9fe6ff;font-weight:600;font-size:13px}
  .pvp-wl{font-weight:700;font-size:13px;color:#c9d4f0}
  .pvp-modes{display:flex;gap:8px}
  .pvp-search{flex:1;font-size:15px;padding:13px 8px}
  .pvp-search:disabled{opacity:.45}
  .pvp-team{background:linear-gradient(180deg,#2b8fd6,#1c5fa8);border-color:#4fb8ff}
  .pvp-search.searching{background:linear-gradient(180deg,#ff6b6f,#c8384c);border-color:#ff9a9a}
  .pvp-note{font-size:12px;line-height:1.45;color:#9aa7d6;text-align:center;padding:0 6px}
  .pvp-online-lbl{font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:#9fb4ff;margin-top:2px}
  .pvp-list{flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;display:flex;flex-direction:column;gap:5px;min-height:100px}
  .pvp-row{display:flex;align-items:center;justify-content:space-between;gap:10px;background:rgba(8,10,22,.55);border:1px solid rgba(124,77,255,.16);border-radius:10px;padding:9px 12px}
  .pvp-row.me{background:rgba(43,224,255,.14);border-color:rgba(43,224,255,.55)}
  .pvp-me-tag{color:#2be0ff;font-size:11px;font-weight:700;margin-left:6px}
  .pvp-p-name{color:#eaf1ff;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .pvp-searching{color:#ffd54a;font-size:11px;margin-left:8px;font-weight:700}
  .pvp-p-stats{color:#9fe6ff;font-weight:700;flex-shrink:0}
  .pvp-empty{text-align:center;color:#9aa7d6;padding:24px 12px;line-height:1.6}`;
  const el = document.createElement("style");
  el.textContent = css;
  document.head.appendChild(el);
}
